import { useTranslation } from 'react-i18next';
import CustomCarousel, { Item } from './CustomCarousel';
import useScreenType from '../../hooks/utility/useScreenType';
import classnames from 'classnames';
import React, { FC, ReactElement, useMemo } from 'react';
import collect from 'collect.js';
import useGoogleReview from '../../hooks/google-review/useGoogleReview';
import GoogleIcon from '../../assets/logo/GoogleIcon';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import { Modals, Routes } from '../../modules/mobile/exports/Enums';
import {
  DEAL_TYPES,
  OfferTypeEnum,
  PROMO_CODE_DISCOUNT_TYPES,
  PROMO_CODE_TYPES,
  REDUX_STORE_SLICES,
  SCREENS,
} from '../../services/exports/Constants';
import useRouter from '../../hooks/navigation/useRouter';
import { CouponDetailsInterface } from '../../services/exports/Interfaces';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useStore from '../../hooks/store/useStore';
import usePromoCode from '../../hooks/order/usePromoCode';
import useAuth from '../../hooks/global/useAuth';
import useModalRouter from '../../modules/mobile/common/hooks/useModalRouter';
import useOffersTranslations from '../../features/offers/hooks/useOffersTranslations';
import useOffersStuff from '../../features/offers/hooks/useOffersStuff';
import DiscountCard, { Variant } from '../discount/DiscountCard';
import useMobileAppSignupBonus from './useMobileAppSignupBonus';
interface Props {
  className?: string;
  itemsClassName?: string;
  dealClassName?: string;
}

const EXPECTED_OFFERS_ORDER_BY_TYPE: OfferTypeEnum[] = [
  OfferTypeEnum.Bogo,
  OfferTypeEnum.FreeItem,
  OfferTypeEnum.PercentageOff,
  OfferTypeEnum.AmountOff,
] as const;

type ItemData = Omit<Item, 'render'> & { render: () => any };

const DealsCarousel = (props: Props): ReactElement => {
  const { className, dealClassName, itemsClassName } = props;

  const { t } = useTranslation(null, { keyPrefix: 'Components:Carousel:DealsCarousel' });
  const { company } = useStore<REDUX_STORE_SLICES.INITIAL_DATA>(REDUX_STORE_SLICES.INITIAL_DATA);
  const { navigate } = useRouter();
  const { isDesktop } = useScreenType();
  const { formatCurrency, toggleLoginModal, toggleOfferModal } = useAppMethods();
  const { openModal } = useModalRouter();
  const { isLoggedIn } = useAuth();
  const { appliedPromoCode, showLoginPrompt, removePromoCode, applyPromoCode } = usePromoCode();
  const { hasGooglePromotion, hasUploadedGoogleReview, googlePromotion } = useGoogleReview({ autoLoad: true });
  const { isMobileApp } = useMobileApp();
  const { getLabelByOffer } = useOffersTranslations();
  const { activeOffers } = useOffersStuff();
  const mobileAppSignupBonusOffer = useMobileAppSignupBonus(dealClassName);

  const onGoogleReviewProgramClick = (): void => {
    navigate({
      mobile: {
        path: Routes.UploadGoogleReview,
      },
      web: {
        to: SCREENS.UPLOAD_GOOGLE_REVIEW_SCREEN,
        options: {
          state: { prevPage: location.pathname },
        },
      },
    });
  };

  const getDiscount = (coupon: CouponDetailsInterface): string | number => {
    switch (coupon?.discount_type) {
      case PROMO_CODE_DISCOUNT_TYPES.PERCENTAGE:
        return `${coupon?.discount}%`;
      case PROMO_CODE_DISCOUNT_TYPES.FIXED_AMOUNT:
        return formatCurrency(coupon?.discount);
    }
  };

  const getPromoCodeDescription = (coupon: CouponDetailsInterface): JSX.Element => {
    if (coupon.type === PROMO_CODE_TYPES.MULTI_PROMO_CODE) {
      return t(`discounts.description.${PROMO_CODE_TYPES.MULTI_PROMO_CODE}`, {
        discount: getDiscount(coupon),
        count: coupon?.components.length,
        code: coupon.code,
      });
    }
    return t(`discounts.description.${PROMO_CODE_TYPES.ONE_TIME_PROMO_CODE}`, {
      discount: getDiscount(coupon),
      code: coupon.code,
    });
  };

  const PromoCodeButton: FC<CouponDetailsInterface> = ({ code }): JSX.Element | null => {
    if (code === appliedPromoCode) {
      return t(`deals.buttons.remove`);
    }
    return t(`deals.buttons.add`);
  };

  const onLogin = (): void => {
    if (isLoggedIn) return;
    if (isMobileApp) {
      return openModal({ id: Modals.Login });
    }
    toggleLoginModal({
      openModal: true,
      onCloseRedirect: () => null,
    });
  };

  const _applyPromoCode = (code: string): void => {
    if (!isLoggedIn) {
      return onLogin();
    }
    applyPromoCode(code);
  };

  const googleDeal = {
    data: googlePromotion,
    render: () => ({
      key: googlePromotion.id,
      title: t(`deals.${DEAL_TYPES.GOOGLE_REVIEW_PROGRAM}.title`, {
        reward: googlePromotion.reward,
      }),
      description: (
        <div className="flex">
          <p className="text-xs text-white my-auto">{t(`deals.${DEAL_TYPES.GOOGLE_REVIEW_PROGRAM}.description`)}</p>
          <div className="p-2xs ml-2xs my-auto rounded-full">
            <GoogleIcon className="h-4 w-4" />
          </div>
        </div>
      ),
      footer: t(`deals.buttons.add`),
      className: classnames(dealClassName, 'cursor-pointer'),
      onClick: onGoogleReviewProgramClick,
    }),
  };

  const promoCodeDeals = collect(company?.promo_codes)
    .map((coupon) => ({
      data: coupon,
      render: () => ({
        key: coupon.code,
        title: t('discounts.title', { discount: getDiscount(coupon) }),
        description: getPromoCodeDescription(coupon),
        footer: <PromoCodeButton {...coupon} />,
        onClick: () => (coupon.code === appliedPromoCode ? removePromoCode() : _applyPromoCode(coupon.code)),
        className: classnames(dealClassName, 'cursor-pointer'),
      }),
    }))
    .toArray();

  const smartPricing = collect(company?.smart_pricing_deals)
    .map((item) => ({
      data: item,
      render: () => ({
        key: item.id,
        title: item.name,
        description: t(`deals.${DEAL_TYPES.SMART_PRICING_RULE}.description`),
        className: dealClassName,
      }),
    }))
    .toArray();

  const offers = collect(activeOffers)
    .sortBy((offer) => EXPECTED_OFFERS_ORDER_BY_TYPE.indexOf(offer.type))
    .map((offer) => ({
      data: offer,
      render: () => ({
        key: offer.id,
        title: getLabelByOffer(offer),
        description: '',
        className: classnames(dealClassName, 'cursor-pointer'),
        onClick: () => {
          if (!isMobileApp) {
            return toggleOfferModal({ openModal: true, offer });
          }

          openModal({
            id: Modals.OfferModal,
            props: {
              offer,
            },
          });
        },
        footer: t('deals.buttons.show_details'),
      }),
    }))
    .toArray();

  const items = useMemo<Item[]>(
    () =>
      collect()
        .when(!!mobileAppSignupBonusOffer, (collection) => collection.push(mobileAppSignupBonusOffer))
        .merge(offers)
        .when(company?.show_master_promo_code, (c) => c.merge(promoCodeDeals))
        .when(hasGooglePromotion && !hasUploadedGoogleReview, (c) => c.push(googleDeal))
        .merge(smartPricing)
        .map((item: ItemData, idx) => ({
          ...item,
          render: () => {
            const { key, ...props } = item.render();

            return <DiscountCard {...props} key={key} variant={((idx % 5) + 1) as Variant} />;
          },
        }))
        .toArray(),
    [
      company?.show_master_promo_code,
      company?.smart_pricing_deals,
      company?.promo_codes,
      hasGooglePromotion,
      hasUploadedGoogleReview,
      googlePromotion,
      appliedPromoCode,
      showLoginPrompt,
      isLoggedIn,
      dealClassName,
      offers,
    ],
  );

  return (
    items.length > 0 && (
      <div className={classnames('relative w-full', className)}>
        <CustomCarousel
          items={items}
          itemsPerSlide={isDesktop ? 2.5 : 1.1}
          slideJump={isDesktop ? 3 : 2}
          autoHeight
          arrowsPosition="top"
          arrowsClassName="lg:mr-lg mr-md"
          itemsClassName={classnames('lg:pl-none pl-md', itemsClassName)}
          title={
            <h5
              className={classnames('lg:ml-none ml-md font-semibold', {
                'text-md': isMobileApp,
              })}
            >
              {t('title')}
            </h5>
          }
          headerClassName="mb-[6px]"
        />
      </div>
    )
  );
};

export default DealsCarousel;
